/**
 * PullRequestsPage (epic-6 §6.5 / GIT-4) — route /repositories/pull-requests.
 *
 * Dedicated cross-repository view of pull requests: pick a repository, filter
 * by status, inspect a PR and its reviews. It reuses the same `repositoriesApi`
 * endpoints as RepositoryDetailPage (list / transitions / reviews) — only the
 * UI shell is new; the PR detail dialog is shared with the repository tab.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { GitPullRequest, ExternalLink, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useGetRepositoriesQuery } from '@/store/api/apiSlice';
import { useGetRepositoryPullRequestsQuery } from '@/store/api/repositoriesApi';
import { PR_STATUS_BADGE, PullRequestDetailDialog } from './PullRequestDetailDialog';
import type { PullRequest, PullRequestStatus, Repository } from '@/types';
import { format } from 'date-fns';

/** Shape-agnostic list normalization (backend may return `[]` or `{ data: [] }`). */
function asList<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  const inner = (result as { data?: unknown } | undefined)?.data;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

const STATUS_OPTIONS: PullRequestStatus[] = ['open', 'reviewing', 'merged', 'closed'];

export function PullRequestsPage() {
  const [repoId, setRepoId] = useState('');
  const [statusFilter, setStatusFilter] = useState<PullRequestStatus | ''>('');
  const [selected, setSelected] = useState<PullRequest | null>(null);

  const { data: repositories, isLoading: reposLoading } = useGetRepositoriesQuery();
  const repoList = asList<Repository>(repositories);
  const activeRepoId = repoId || repoList[0]?._id || '';

  const {
    data: prs,
    isLoading: prsLoading,
    isFetching: prsFetching,
    refetch: refetchPrs,
  } = useGetRepositoryPullRequestsQuery(
    { id: activeRepoId, status: statusFilter || undefined },
    { skip: !activeRepoId },
  );

  const prList = (prs as PullRequest[] | undefined) ?? [];

  const columns: ColumnDef<PullRequest, unknown>[] = [
    {
      accessorKey: 'providerNumber',
      header: '#',
      cell: ({ row }) => (
        <span className="font-mono text-xs">#{row.original.providerNumber}</span>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-sm">{row.original.title}</p>
          <p className="text-xs text-muted-foreground">
            {row.original.headBranch} → {row.original.baseBranch}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={PR_STATUS_BADGE[row.original.status]} className="text-xs">
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'reviewConversationId',
      header: 'Review conv.',
      cell: ({ row }) =>
        row.original.reviewConversationId ? (
          <Link
            to={`/conversations/${row.original.reviewConversationId}`}
            className="text-xs text-muted-foreground hover:text-primary"
            onClick={(e) => e.stopPropagation()}
          >
            open <ExternalLink className="inline h-3 w-3" />
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pull Requests</h1>
          <p className="text-sm text-muted-foreground">
            Review pull requests across your indexed repositories (GIT-4)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => activeRepoId && refetchPrs()}
          disabled={!activeRepoId || prsFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${prsFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-1.5 text-sm">
            <GitPullRequest className="h-4 w-4" /> Pull requests
          </CardTitle>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label htmlFor="pr-repo" className="text-xs text-muted-foreground">
                Repository
              </label>
              <select
                id="pr-repo"
                className="flex h-9 min-w-[16rem] rounded-md border border-input bg-background px-3 text-sm"
                value={activeRepoId}
                onChange={(e) => {
                  setRepoId(e.target.value);
                  setSelected(null);
                }}
                disabled={reposLoading || repoList.length === 0}
              >
                {repoList.length === 0 && <option value="">No repositories</option>}
                {repoList.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="pr-status" className="text-xs text-muted-foreground">
                Status
              </label>
              <select
                id="pr-status"
                className="flex h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as PullRequestStatus | '')}
              >
                <option value="">All</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {reposLoading ? (
            <LoadingSpinner className="py-12" />
          ) : repoList.length === 0 ? (
            <EmptyState
              title="No repositories"
              description="Add a repository before reviewing its pull requests"
              action={
                <Button asChild>
                  <Link to="/repositories">Go to Repositories</Link>
                </Button>
              }
            />
          ) : prsLoading ? (
            <LoadingSpinner className="py-12" />
          ) : prList.length === 0 ? (
            <EmptyState
              title="No pull requests"
              description="No pull requests match the selected repository and status"
            />
          ) : (
            <DataTable columns={columns} data={prList} onRowClick={setSelected} />
          )}
        </CardContent>
      </Card>

      {selected && activeRepoId && (
        <PullRequestDetailDialog
          repoId={activeRepoId}
          pr={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
